/**
 * Experiments Configuration
 *
 * Current experiments and intentional previews. Retired assignment history lives
 * in PostHog and docs/experiments/2026-09-07_retired-experiment-cleanup.md.
 */

type ExperimentStatus = 'planned' | 'draft' | 'active' | 'paused' | 'completed' | 'archived';

type ExperimentConfig = {
  name: string;
  featureFlag?: string;
  description: string;
  variants?: Record<string, string>;
  metrics?: string[];
  posthog_metrics?: {
    primary?: string | string[];
    secondary?: string[];
  };
  primaryMetric?: string;
  secondaryMetrics?: string[];
  startDate?: string;
  endDate?: string;
  status: ExperimentStatus;
  type?: string;
  hypothesis?: string;
  target_lift?: Record<string, number>;
  sample_size_needed?: string;
  notes?: string;
  result?: string;
  posthog_experiment_id?: number;
};

type ExperimentRegistry = Record<string, ExperimentConfig>;

// Active Experiments Registry
export const ACTIVE_EXPERIMENTS = {
  CLARITY_HOME_CURRENT_EDITED_2026_09_22: {
    name: 'homepage_clarity_current_edited_2026_09_22',
    featureFlag: 'homepage-clarity-current-edited-2026-09-22',
    description: 'A: current live Clarity homepage. B: confirmed edited c600694b7 homepage.',
    variants: { control: 'A — Current live Clarity', test: 'B — Confirmed edited Clarity' },
    status: 'draft',
    type: 'posthog_experiment',
    posthog_experiment_id: 466537,
    primaryMetric: 'Completed signup within 7 days',
    secondaryMetrics: ['Checkout started within 7 days', 'Payment received within 14 days'],
    metrics: ['botspot_signup_completed', 'InitiateCheckout', 'PaymentReceived'],
    notes:
      'Staged only. Flag inactive. Canonical-root assignment and visible post-render exposure are wired. Local previews are deterministic and untracked. Custom experiment_exposure is filtered to this flag. Author receipt, remote publication and signup identity continuity remain to verify. Preserve predecessor 457670.',
  },
  WEBSITE_THEME_HOME: {
    name: 'website_theme_home_2026_09_12',
    featureFlag: 'website-theme-home-2026-09-12',
    description:
      'Dark versus white appearance for visitors first entering through home; one persistent cohort across the funnel.',
    variants: { control: 'Dark mode', test: 'White mode' },
    metrics: ['Lead', 'botspot_lead_created', 'InitiateCheckout', 'PaymentReceived'],
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 463634,
    notes:
      'Production-only rendered exposure. Manual preferences excluded at enrollment. Cohort persists through signup and payment. See docs/experiments/2026-09-12-website-theme-experiments.md.',
  },
  WEBSITE_THEME_BOOTCAMP: {
    name: 'website_theme_bootcamp_2026_09_12',
    featureFlag: 'website-theme-bootcamp-2026-09-12',
    description:
      'Dark versus white appearance for visitors first entering through bootcamp; one persistent cohort across the funnel.',
    variants: { control: 'Dark mode', test: 'White mode' },
    metrics: ['Lead', 'botspot_lead_created', 'InitiateCheckout', 'PaymentReceived'],
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 463635,
    notes:
      'Production-only rendered exposure. Manual preferences excluded at enrollment. Cohort persists through signup and payment. See docs/experiments/2026-09-12-website-theme-experiments.md.',
  },
  WEBSITE_THEME_SELF_PACED: {
    name: 'website_theme_self_paced_2026_09_12',
    featureFlag: 'website-theme-self-paced-2026-09-12',
    description:
      'Dark versus white appearance for visitors first entering through self-paced; one persistent cohort across the funnel.',
    variants: { control: 'Dark mode', test: 'White mode' },
    metrics: ['Lead', 'botspot_lead_created', 'InitiateCheckout', 'PaymentReceived'],
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 463636,
    notes:
      'Production-only rendered exposure. Manual preferences excluded at enrollment. Cohort persists through signup and payment. See docs/experiments/2026-09-12-website-theme-experiments.md.',
  },
  WEBSITE_THEME_CHALLENGE: {
    name: 'website_theme_challenge_2026_09_12',
    featureFlag: 'website-theme-challenge-2026-09-12',
    description:
      'Dark versus white appearance for visitors first entering through challenge; one persistent cohort across the funnel.',
    variants: { control: 'Dark mode', test: 'White mode' },
    metrics: ['Lead', 'botspot_lead_created', 'InitiateCheckout', 'PaymentReceived'],
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 463637,
    notes:
      'Production-only rendered exposure. Manual preferences excluded at enrollment. Cohort persists through signup and payment. See docs/experiments/2026-09-12-website-theme-experiments.md.',
  },
  WEBSITE_THEME_PRICING: {
    name: 'website_theme_pricing_2026_09_12',
    featureFlag: 'website-theme-pricing-2026-09-12',
    description:
      'Dark versus white appearance for visitors first entering through pricing; one persistent cohort across the funnel.',
    variants: { control: 'Dark mode', test: 'White mode' },
    metrics: ['Lead', 'botspot_lead_created', 'InitiateCheckout', 'PaymentReceived'],
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 463638,
    notes:
      'Production-only rendered exposure. Manual preferences excluded at enrollment. Cohort persists through signup and payment. See docs/experiments/2026-09-12-website-theme-experiments.md.',
  },
  WEBSITE_THEME_SIGNUP: {
    name: 'website_theme_signup_2026_09_12',
    featureFlag: 'website-theme-signup-2026-09-12',
    description:
      'Dark versus white appearance for visitors first entering through signup; one persistent cohort across the funnel.',
    variants: { control: 'Dark mode', test: 'White mode' },
    metrics: ['Lead', 'botspot_lead_created', 'InitiateCheckout', 'PaymentReceived'],
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 463639,
    notes:
      'Production-only rendered exposure. Manual preferences excluded at enrollment. Cohort persists through signup and payment. See docs/experiments/2026-09-12-website-theme-experiments.md.',
  },
  WEBSITE_THEME_GOOGLE_SEARCH: {
    name: 'website_theme_google_search_2026_09_12',
    featureFlag: 'website-theme-google-search-2026-09-12',
    description:
      'Dark versus white appearance for visitors first entering through google-search; one persistent cohort across the funnel.',
    variants: { control: 'Dark mode', test: 'White mode' },
    metrics: ['Lead', 'botspot_lead_created', 'InitiateCheckout', 'PaymentReceived'],
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 463640,
    notes:
      'Production-only rendered exposure. Manual preferences excluded at enrollment. Cohort persists through signup and payment. See docs/experiments/2026-09-12-website-theme-experiments.md.',
  },
  WEBSITE_THEME_MARKETPLACE: {
    name: 'website_theme_marketplace_2026_09_12',
    featureFlag: 'website-theme-marketplace-2026-09-12',
    description:
      'Dark versus white appearance for visitors first entering through marketplace; one persistent cohort across the funnel.',
    variants: { control: 'Dark mode', test: 'White mode' },
    metrics: ['Lead', 'botspot_lead_created', 'InitiateCheckout', 'PaymentReceived'],
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 463641,
    notes:
      'Production-only rendered exposure. Manual preferences excluded at enrollment. Cohort persists through signup and payment. See docs/experiments/2026-09-12-website-theme-experiments.md.',
  },
  WEBSITE_THEME_CHECKOUT: {
    name: 'website_theme_checkout_2026_09_12',
    featureFlag: 'website-theme-checkout-2026-09-12',
    description:
      'Dark versus white appearance for visitors first entering through checkout; one persistent cohort across the funnel.',
    variants: { control: 'Dark mode', test: 'White mode' },
    metrics: ['Lead', 'botspot_lead_created', 'InitiateCheckout', 'PaymentReceived'],
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 463642,
    notes:
      'Production-only rendered exposure. Manual preferences excluded at enrollment. Cohort persists through signup and payment. See docs/experiments/2026-09-12-website-theme-experiments.md.',
  },
  AI_TRADING_BOOTCAMP_COURSE_NAME: {
    name: 'ai_trading_bootcamp_course_name',
    featureFlag: 'ai-trading-bootcamp-course-name',
    description:
      'Compare AI Trading Bootcamp with Agentic Trading Bootcamp while holding the promise, page, curriculum, price, and calls to action constant.',
    variants: {
      control: 'AI Trading Bootcamp',
      agentic: 'Agentic Trading Bootcamp',
    },
    metrics: ['BootcampCheckoutStarted', 'PaymentReceived', 'BootcampPathSelected'],
    posthog_metrics: {
      primary: 'BootcampCheckoutStarted',
      secondary: [
        'Live Bootcamp PaymentReceived per exposed user',
        'Live Bootcamp revenue per exposed user',
        'Bootcamp alternate path selections per exposed user',
      ],
    },
    primaryMetric: 'Live Bootcamp checkout starts per exposed user',
    secondaryMetrics: [
      'Live Bootcamp PaymentReceived per exposed user',
      'Live Bootcamp revenue per exposed user',
      'Bootcamp alternate path selections per exposed user',
    ],
    startDate: '2026-08-05',
    status: 'draft',
    type: 'posthog_experiment',
    posthog_experiment_id: 402059,
    hypothesis:
      'Agentic Trading Bootcamp will increase qualified live-course checkout starts versus AI Trading Bootcamp because agentic trading has stronger current category momentum.',
    notes:
      'Draft in PostHog with a 50/50 control-agentic split, experience continuity, and test-account exclusion. Keep the flag inactive until this code is deployed. Direct review routes are /courses/ai-trading-bootcamp/control and /courses/ai-trading-bootcamp/agentic. Payment metrics filter plan_slug=ai-bootcamp; validate that exact property on the first settled live-bootcamp purchase before declaring a winner.',
  },

  // Free tier landing (Rob, 2026-10-17): where a new free tier user lands
  // right after signup. Only the free tier arm of free-tier-v1 is enrolled;
  // checkout arm users and visitors with a chosen destination never are.
  // Flag off, missing or unknown: the dashboard (today's behavior). The spot
  // arm serves the dashboard until Spot is on the build, and the exposure
  // records served_variant and landing_fallback_reason.
  FREE_TIER_LANDING_2026_10: {
    name: 'free_tier_landing_2026_10',
    featureFlag: 'free-tier-landing-2026-10',
    description:
      'New free tier users land on a new AI Agent chat, Spot, or the dashboard right after signup.',
    variants: {
      ai_agent: 'A: new AI Agent chat (/ai-agent)',
      spot: 'B: Spot (/ai-agent?spot=open)',
      dashboard: 'C: dashboard (/dashboard, today)',
    },
    metrics: ['PaymentReceived', 'broker_connected', 'first_real_money_trade'],
    status: 'draft',
    type: 'posthog_experiment',
    hypothesis:
      'Landing new free users in a conversation (AI Agent or Spot) gets more of them to talk to the agent, connect a broker and upgrade than the dashboard does.',
    notes:
      'Code only. The PostHog flag is created by the team after the code is live (team rule, 2026-09-30). Read money over time, broker connected and first real-money trade per arm (Rob, 2026-10-16).',
  },

  // Light vs Starter Checkout - Pure price test at the /checkout/starter-plan landing
  // page (the one users hit from the sales funnel). Replaces STARTER_PRICING_ABC.
  //
  // control: $99 strikethrough → $49/month (Starter, discounttest-2 winner of old test)
  // test:    $49 strikethrough → $24/month (Light, new lower tier)
  //
  // Both variants share identical UX: same 2-hour urgency timer, same 50%-off framing,
  // same "no trial" and "30-day money-back" copy, same CTA "Claim 50% Off Now".
  // The ONLY variable is the underlying product + price. Pure pricing experiment.
  //
  // Note: /checkout/light-plan (reached from Light CTA on /pricing) always shows Light
  // regardless of this flag — the user explicitly chose Light. This flag only governs
  // /checkout/starter-plan (the sales-funnel checkout surface).
  //
  // Primary metric: PaymentReceived (server-side, reliable) rather than Purchase
  // (client-side, currently lossy due to post-checkout hard redirect).
  LIGHT_VS_STARTER_CHECKOUT: {
    name: 'light_vs_starter_checkout',
    featureFlag: 'light-vs-starter-checkout',
    description:
      'Pure price test on /checkout/starter-plan: $99→$49 Starter (control) vs $49→$24 Light (test). Same UX otherwise.',
    variants: {
      control: 'Starter $99 → $49 (discounttest-2 winner of previous test)',
      test: 'Light $49 → $24 (new lower tier)',
    },
    metrics: ['PaymentReceived', 'Purchase'],
    posthog_metrics: {
      primary: 'PaymentReceived',
      secondary: ['Purchase'],
    },
    startDate: '2026-04-22',
    status: 'active',
    type: 'posthog_experiment',
    hypothesis:
      'A $24 entry-level tier captures price-sensitive users who currently bounce at $49. Expected lift: 30-100% on PaymentReceived per exposed user. Cannibalization of Starter revenue is acceptable if Light stays meaningfully constrained on AI usage while still giving users one deployment to activate.',
    notes:
      'Replaces STARTER_PRICING_ABC (which ran Feb-Apr 2026 and concluded: $49 no-trial won 2.47x vs $99 no-trial). Running in PostHog; verified 2026-09-07. /checkout/light-plan is outside the experiment — pricing-page Light CTAs always route users to the Light variant by pathname. Light includes one deployment but materially lower AI/revision capacity than Starter.',
  },

  // Two approved benefits for the same optional 30-minute team call.
  // Missing flags render setup copy. This never changes the phone policy or
  // the independent landing-page design assignment.
  CHALLENGE_BOOKING_SETUP_VS_PROJECT: {
    name: 'challenge_booking_setup_vs_project',
    featureFlag: 'challenge-booking-setup-vs-project',
    description:
      'Compare two honest benefits for the same free 30-minute team call after challenge registration.',
    variants: {
      control: 'Get help setting up for the AI Trading Challenge',
      test: 'Make a plan for your first AI trading project',
    },
    metrics: ['expert_call_booked', 'PaymentReceived', 'Purchase'],
    posthog_metrics: {
      primary: 'expert_call_booked per exposed registrant',
      secondary: ['Purchase'],
    },
    startDate: '2026-09-07',
    status: 'draft',
    type: 'posthog_experiment',
    hypothesis:
      'A concrete setup or project benefit may increase suitable held calls and collected course revenue. No numerical lift is assumed.',
    notes:
      'Draft until measurement and allocation are approved. Both arms share the team calendar, event details and optional course route. Evaluate collected payments, refunds and host cost before declaring a winner.',
  },

  // Post-signup destination experiment. Assignment is evaluated only on the
  // dedicated /post-signup route after the authenticated attribution save has
  // been read back from the user profile. Landing pages never own this split.
  POST_SIGNUP_CHECKOUT_GATE_V1: {
    name: 'post_signup_checkout_gate_v1',
    featureFlag: 'post_signup_checkout_gate_v1_variant',
    description:
      'Current post-signup destination versus immediate Starter/Light checkout after required self-reported attribution.',
    variants: {
      control:
        'Continue to the existing chooser, saved prompt, handoff, or other intended product destination',
      test: 'Go immediately to the Starter/Light checkout experiment while preserving the intended destination',
    },
    metrics: ['PaymentReceived', 'Purchase'],
    posthog_metrics: {
      primary: 'PaymentReceived with is_first_payment=true per exposed user',
      secondary: [
        'Purchase per exposed user',
        'PaymentReceived revenue per exposed user',
        'InitiateCheckout per exposed user',
      ],
    },
    startDate: '2026-08-09',
    status: 'active',
    type: 'posthog_experiment',
    hypothesis:
      'Requiring payment immediately after signup and attribution will increase first positive Starter/Light payments versus the current product-choice path.',
    notes:
      'Assignment happens only after user.howFoundUs is persisted. The Light-vs-Starter and Stripe-hosted experiments remain nested inside checkout. Direct Challenge registrations are excluded. Rob, 2026-10-14: React no longer evaluates this flag. /post-signup sends the free tier arm (Node freeTier.enabled) to the dashboard and every other new user to the immediate checkout.',
  },

  STRIPE_HOSTED_CHECKOUT_2026_08: {
    name: 'stripe_hosted_checkout_2026_08',
    featureFlag: 'stripe-hosted-checkout-2026-08',
    description:
      'Stable current checkout versus Stripe-managed full-page Checkout across every active purchase route.',
    variants: {
      control: 'Stable current checkout',
      stripe_hosted: 'Stripe-managed full-page Checkout',
    },
    metrics: [
      'CheckoutExperienceConverted',
      'PaymentReceived',
      'checkout_experience_launch_failed',
      'checkout_experience_session_expired',
      'checkout_experience_cancelled',
    ],
    posthog_metrics: {
      primary: 'CheckoutExperienceConverted per exposed subject',
      secondary: [
        'PaymentReceived per exposed subject',
        'Hosted Session launch failure per exposed subject',
        'Hosted Checkout redirect per exposed subject',
        'Hosted Checkout cancellation per exposed subject',
      ],
    },
    startDate: '2026-08-25',
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 446860,
    hypothesis:
      'Stripe-managed full-page Checkout will increase completed paid checkouts and reduce launch/payment failures versus custom checkout ownership.',
    notes:
      'Running in PostHog at 50/50 on production checkout hosts. One exact multivariate flag across /checkout, /checkout/starter-plan, and /checkout/light-plan. Includes guests. False, null, invalid, or timed-out flag values render unenrolled control and emit no exposure. CheckoutExperienceConverted uses the frozen exposure subject for both variants; PaymentReceived remains a revenue guardrail.',
  },

  // Pricing Page Redesign
  // Presentation-only test. Product catalog, price, checkout routing, coupons,
  // and downstream checkout experiments remain unchanged between variants.
  PRICING_PAGE_REDESIGN_2026_07: {
    name: 'pricing_page_redesign_2026_07',
    featureFlag: 'pricing-page-redesign-2026-07',
    description:
      'Testing the current pricing page against an outcome-first layout with a compact capacity and support comparison.',
    variants: {
      control: 'Current pricing page',
      test: 'Outcome-first pricing page with compact capacity comparison and separated training and enterprise offers',
    },
    metrics: ['PaymentReceived', 'pricing_cta_click', 'PaymentFailed', 'stripe_confirm_failed'],
    posthog_metrics: {
      primary: 'PaymentReceived per exposed user',
      secondary: [
        'PaymentReceived revenue per exposed user',
        'pricing_cta_click per exposed user',
        'Plan mix by PaymentReceived product',
        'PaymentFailed per exposed user',
        'Stripe confirm failure per exposed user',
      ],
    },
    startDate: '2026-07-14',
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 383736,
    hypothesis:
      'Clear workload limits and stronger plan differentiation will increase paid conversion without reducing revenue per exposed user.',
    target_lift: {
      payment_received: 15,
      revenue_per_exposed_user: 0,
    },
    notes:
      'Launched in PostHog on 2026-07-14 with 50% control and 50% test allocation across eligible traffic. Do not change price, product, coupon, or checkout targeting as part of this experiment.',
  },

  // Fresh replacement: identical auth methods and routing; only presentation changes.
  GOOGLE_SEARCH_AUTH_FIRST_PERSONALIZATION_2026_08: {
    name: 'google_search_auth_first_personalization_2026_08',
    featureFlag: 'google_search_auth_first_personalization_2026_08_variant',
    description:
      'Generic auth-first dialog versus job-customized auth-first layout on the Google Search landing page.',
    variants: {
      generic_auth_first:
        'Standard Sign in to BotSpot dialog with Google, Apple, and email authentication',
      customized_auth_first:
        'Job-matched split-screen page with the same Google, Apple, and email authentication',
    },
    metrics: ['Lead', 'PaymentReceived'],
    posthog_metrics: {
      primary: 'Lead',
      secondary: ['PaymentReceived'],
    },
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 449287,
    hypothesis:
      'Job-customized auth-first presentation will increase canonical Lead conversion versus generic auth-first without reducing confirmed paid revenue.',
    notes:
      'Running at 50/50 in PostHog; verified 2026-09-07. Preserve google_search_lp_job as a diagnostic breakdown.',
  },
} satisfies ExperimentRegistry;

const getExperimentFromRegistry = (
  registry: ExperimentRegistry,
  experimentKey: string,
): ExperimentConfig | undefined => registry[experimentKey];

// Experiment Status Types
export const EXPERIMENT_STATUS = {
  PLANNED: 'planned',
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  ARCHIVED: 'archived',
} as const;

// Helper function to get experiment configuration
export const getExperiment = (experimentKey: string): ExperimentConfig | undefined => {
  return getExperimentFromRegistry(ACTIVE_EXPERIMENTS, experimentKey);
};

// Helper function to get all active experiments
export const getActiveExperiments = (): ExperimentRegistry => {
  return Object.entries(ACTIVE_EXPERIMENTS)
    .filter(([, experiment]) => experiment.status === EXPERIMENT_STATUS.ACTIVE)
    .reduce<ExperimentRegistry>((acc, [key, experiment]) => {
      acc[key] = experiment;
      return acc;
    }, {});
};

// Helper function to track experiment conversion
type ExperimentConversionValue = string | number | boolean | null | undefined;
type ExperimentConversionAdditionalData = Readonly<{
  [key: string]: ExperimentConversionValue;
}>;
const EMPTY_EXPERIMENT_CONVERSION_DATA: ExperimentConversionAdditionalData = {};

export const trackExperimentConversion = (
  experimentName: string,
  variant: string,
  conversionEvent: string,
  additionalData: ExperimentConversionAdditionalData = EMPTY_EXPERIMENT_CONVERSION_DATA,
) => {
  console.log(
    `🎯 [Experiment Conversion] ${experimentName} - ${variant} - ${conversionEvent}`,
    additionalData,
  );

  // This could be enhanced to send to your analytics system
  return {
    experiment: experimentName,
    variant,
    conversion: conversionEvent,
    timestamp: new Date().toISOString(),
    ...additionalData,
  };
};

/**
 * Usage Examples:
 *
 * 1. Basic A/B Test in a Component (PostHog Experiment):
 * ```javascript
 * import useABTest from '../hooks/useABTest';
 * import { ACTIVE_EXPERIMENTS } from '../utils/experiments';
 *
 * const MyComponent = () => {
 *   const experiment = ACTIVE_EXPERIMENTS.PRICING_PAGE_REDESIGN_2026_07;
 *   const { variant, isLoading, isTest, isControl } = useABTest(
 *     experiment.name,
 *     experiment.featureFlag,
 *     'control'
 *   );
 *
 *   if (isLoading) return <Loading />;
 *
 *   return isTest ? <NewComponent /> : <OriginalComponent />;
 *   // OR: return variant === 'test' ? <NewComponent /> : <OriginalComponent />;
 * };
 * ```
 *
 * 2. Conditional Rendering (PostHog Experiment):
 * ```javascript
 * const { isTest } = useABTest('my_test', 'my_flag');
 *
 * return (
 *   <div>
 *     {isTest && <NewFeature />}
 *     <ExistingContent />
 *   </div>
 * );
 * ```
 *
 * 3. String-based Variants:
 * ```javascript
 * // PostHog flag returns: "red", "blue", or "green"
 * const { variant } = useABTest('button_color_test', 'button_color_flag', 'red');
 *
 * return <Button color={variant}>Click me</Button>;
 * ```
 */
